import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiAlertCircle,
  FiCheck,
  FiGlobe,
  FiImage,
  FiLoader,
  FiSave,
  FiX,
} from 'react-icons/fi';
import { api } from '../services/api';
import { richTextToPlainText } from '../utils/richText';
import ProductRichTextEditor from './ProductRichTextEditor';
import './AdminProductDescriptionTranslation.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];

const resolveMediaUrl = (value) => (
  value && value.startsWith('/') ? `${API_URL}${value}` : value
);

const valueForLocale = (localeCode, items) => (
  (items || []).find((item) => item.locale === localeCode)?.content || ''
);

const emitSaveState = (articleId, locale, state) => {
  if (typeof window === 'undefined') return;
  if (!window.__haryadTranslationSaveStates) window.__haryadTranslationSaveStates = {};
  const key = `article:${String(articleId)}:${locale}:content`;
  window.__haryadTranslationSaveStates[key] = state;
  window.dispatchEvent(new CustomEvent('haryad:translation-save-state', {
    detail: {
      entityType: 'article',
      entityId: String(articleId),
      locale,
      fieldPath: 'content',
      state,
    },
  }));
};

const AdminArticleContentTranslation = ({ articleId, sourceContent = '', onClose }) => {
  const [loading, setLoading] = useState(true);
  const [locales, setLocales] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [activeLocale, setActiveLocale] = useState('');
  const [value, setValue] = useState('');
  const [saveState, setSaveState] = useState('idle');
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState('');
  const saveTimers = useRef({});
  const pendingSaves = useRef({});
  const saveChains = useRef({});
  const requestNumbers = useRef({});
  const activeLocaleRef = useRef('');
  const mountedRef = useRef(true);

  const enabledLocales = useMemo(
    () => locales.filter((locale) => locale.enabled !== false && locale.code !== 'fa'),
    [locales]
  );
  const activeLanguage = enabledLocales.find((locale) => locale.code === activeLocale);
  const activeTranslation = translations.find((item) => item.locale === activeLocale);
  const sourceText = richTextToPlainText(sourceContent);
  const sourceExcerpt = sourceText.slice(0, 220);

  const updateLocalTranslation = useCallback((localeCode, content, extra = {}) => {
    setTranslations((items) => {
      const existing = items.find((item) => item.locale === localeCode);
      const updated = {
        ...(existing || { locale: localeCode, status: 'draft' }),
        content,
        hasUnpublishedChanges: true,
        ...extra,
      };
      return existing
        ? items.map((item) => (item === existing ? updated : item))
        : [...items, updated];
    });
  }, []);

  const persistPending = useCallback(async (localeCode) => {
    const pending = pendingSaves.current[localeCode];
    if (!pending) {
      try {
        await saveChains.current[localeCode];
        return true;
      } catch (_) {
        return false;
      }
    }

    delete pendingSaves.current[localeCode];
    if (activeLocaleRef.current === localeCode && mountedRef.current) setSaveState('saving');
    emitSaveState(articleId, localeCode, 'saving');
    const previous = saveChains.current[localeCode] || Promise.resolve();
    const request = previous
      .catch(() => undefined)
      .then(() => api.patch(`/api/articles/${articleId}/translations/${localeCode}/field`, {
        path: 'content',
        value: pending.value,
      }));
    saveChains.current[localeCode] = request;

    try {
      const response = await request;
      emitSaveState(articleId, localeCode, 'saved');
      if (!mountedRef.current) return true;
      updateLocalTranslation(localeCode, pending.value, {
        status: response.data?.status || 'draft',
        updatedAt: response.data?.updatedAt || new Date().toISOString(),
      });
      if (
        activeLocaleRef.current === localeCode
        && requestNumbers.current[localeCode] === pending.requestNumber
        && !pendingSaves.current[localeCode]
      ) {
        setSaveState('saved');
      }
      window.dispatchEvent(new CustomEvent('haryad:translations-changed', {
        detail: { entityType: 'article', entityId: articleId },
      }));
      return true;
    } catch (saveError) {
      emitSaveState(articleId, localeCode, 'error');
      if (mountedRef.current) {
        if (activeLocaleRef.current === localeCode) setSaveState('error');
        setError(saveError.data?.message || saveError.message || 'ذخیره پیش‌نویس متن مقاله انجام نشد');
      }
      return false;
    }
  }, [articleId, updateLocalTranslation]);

  const flushLocale = useCallback(async (localeCode) => {
    if (!localeCode) return true;
    if (saveTimers.current[localeCode]) {
      clearTimeout(saveTimers.current[localeCode]);
      delete saveTimers.current[localeCode];
    }
    return persistPending(localeCode);
  }, [persistPending]);

  useEffect(() => {
    const handleFlush = (event) => {
      const detail = event.detail || {};
      if (detail.entityType !== 'article' || String(detail.entityId) !== String(articleId)) return;
      if (!detail.locale) return;
      flushLocale(detail.locale);
    };
    window.addEventListener('haryad:flush-translations', handleFlush);
    return () => window.removeEventListener('haryad:flush-translations', handleFlush);
  }, [articleId, flushLocale]);


  const scheduleSave = (nextValue, localeCode) => {
    const requestNumber = (requestNumbers.current[localeCode] || 0) + 1;
    requestNumbers.current[localeCode] = requestNumber;
    pendingSaves.current[localeCode] = { value: nextValue, requestNumber };
    if (saveTimers.current[localeCode]) clearTimeout(saveTimers.current[localeCode]);
    setSaveState('waiting');
    emitSaveState(articleId, localeCode, 'waiting');
    setError('');
    updateLocalTranslation(localeCode, nextValue);
    saveTimers.current[localeCode] = setTimeout(() => {
      delete saveTimers.current[localeCode];
      persistPending(localeCode);
    }, 700);
  };

  const selectLocale = async (localeCode) => {
    if (!localeCode || localeCode === activeLocaleRef.current || switching) return;
    setSwitching(true);
    const saved = await flushLocale(activeLocaleRef.current);
    if (!saved) {
      setSwitching(false);
      return;
    }
    activeLocaleRef.current = localeCode;
    setActiveLocale(localeCode);
    setValue(valueForLocale(localeCode, translations));
    setSaveState('idle');
    setError('');
    setSwitching(false);
  };

  const uploadLocalizedImage = async (file) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      throw new Error('فرمت تصویر باید JPG، PNG، WEBP، GIF یا AVIF باشد.');
    }
    if (file.size > MAX_IMAGE_SIZE) {
      throw new Error('حجم تصویر نباید بیشتر از ۵ مگابایت باشد.');
    }
    if (!activeLocale) throw new Error('ابتدا زبان متن مقاله را انتخاب کنید.');

    const formData = new FormData();
    formData.append('image', file);
    const response = await api.upload(
      `/api/articles/${articleId}/translations/${activeLocale}/content-images`,
      formData
    );
    const imageUrl = response.data?.imageUrl;
    if (!response.success || !imageUrl) throw new Error('آپلود تصویر انجام نشد.');
    return resolveMediaUrl(imageUrl);
  };

  const uploadArticleVideo = async (file) => {
    const formData = new FormData();
    formData.append('video', file);
    const response = await api.upload('/api/articles/upload-video', formData);
    const videoUrl = response.data?.videoUrl;
    if (!response.success || !videoUrl) throw new Error('آپلود ویدیو انجام نشد.');
    return resolveMediaUrl(videoUrl);
  };


  const close = async () => {
    setSwitching(true);
    const saved = await flushLocale(activeLocaleRef.current);
    setSwitching(false);
    if (saved) onClose?.();
  };

  useEffect(() => {
    mountedRef.current = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [localeResponse, translationResponse] = await Promise.all([
          api.get('/api/locales'),
          api.get(`/api/articles/${articleId}/translations`),
        ]);
        if (!mountedRef.current) return;
        const localeItems = Array.isArray(localeResponse.data) ? localeResponse.data : [];
        const translationItems = Array.isArray(translationResponse.data) ? translationResponse.data : [];
        const firstLocale = localeItems.find((item) => item.enabled !== false && item.code !== 'fa')?.code || '';
        setLocales(localeItems);
        setTranslations(translationItems);
        activeLocaleRef.current = firstLocale;
        setActiveLocale(firstLocale);
        setValue(valueForLocale(firstLocale, translationItems));
      } catch (loadError) {
        if (mountedRef.current) {
          setError(loadError.data?.message || loadError.message || 'دریافت متن مقاله در زبان‌های دیگر انجام نشد');
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };
    const timers = saveTimers.current;
    const pending = pendingSaves.current;
    load();
    return () => {
      mountedRef.current = false;
      Object.entries(timers).forEach(([localeCode, timer]) => {
        clearTimeout(timer);
        delete timers[localeCode];
      });
      Object.keys(pending).forEach((localeCode) => {
        persistPending(localeCode);
      });
    };
  }, [articleId, persistPending]);

  return (
    <section className="admin-product-description-translation admin-article-content-translation" aria-label="متن چندزبانه مقاله" dir="rtl">
      <header>
        <span className="admin-product-description-translation__icon"><FiGlobe /><FiImage /></span>
        <div>
          <h4>متن کامل مقاله در زبان‌های دیگر</h4>
          <p>متن، تصاویر، ویدیوها، لینک‌ها و چینش هر زبان کاملاً مستقل ذخیره می‌شوند.</p>
        </div>
        <button type="button" className="ui-close-button" onClick={close} disabled={switching} aria-label="بستن ویرایشگر ترجمه">
          {switching ? <FiLoader /> : <FiX />}
        </button>
      </header>

      {loading ? (
        <div className="admin-product-description-translation__loading"><FiLoader /> در حال دریافت زبان‌ها و پیش‌نویس‌ها...</div>
      ) : (
        <>
          <div className="admin-product-description-translation__tabs" role="tablist" aria-label="انتخاب زبان متن مقاله">
            {enabledLocales.map((locale) => {
              const translation = translations.find((item) => item.locale === locale.code);
              const localizedContent = translation?.content || '';
              const filled = Boolean(
                richTextToPlainText(localizedContent)
                || /<img\b|data-article-video/i.test(localizedContent)
              );
              const stale = Boolean(translation?.isStale || translation?.isPublishedStale);
              return (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeLocale === locale.code}
                  className={`${activeLocale === locale.code ? 'is-active' : ''} ${filled ? 'is-filled' : ''} ${stale ? 'is-stale' : ''}`}
                  key={locale.code}
                  onClick={() => selectLocale(locale.code)}
                  disabled={switching}
                >
                  <b>{locale.code.toUpperCase()}</b>
                  <span>{locale.nativeName}</span>
                  {filled ? <FiCheck /> : null}
                  {stale ? <small>نیاز به بازبینی</small> : null}
                </button>
              );
            })}
          </div>

          {activeLanguage ? (
            <div className="admin-product-description-translation__body">
              <div className="admin-product-description-translation__source">
                <FiAlertCircle />
                <span>
                  <strong>نسخهٔ فارسی به‌صورت خودکار کپی نمی‌شود.</strong>
                  <small>{sourceExcerpt ? `خلاصه متن فارسی: ${sourceExcerpt}${sourceText.length > 220 ? '…' : ''}` : 'متن فارسی مقاله هنوز خالی است.'}</small>
                </span>
              </div>

              <div className="admin-product-description-translation__editor-head">
                <div>
                  <strong>ویرایشگر {activeLanguage.nativeName}</strong>
                  <small>تصویر را در محل نشانگر تایپ آپلود کنید؛ فایل در پوشهٔ مستقل همین زبان قرار می‌گیرد.</small>
                </div>
                <span className={`is-${saveState}`}>
                  {saveState === 'idle' && (value ? 'آماده ویرایش' : 'پیش‌نویس خالی')}
                  {saveState === 'waiting' && 'در انتظار توقف تایپ...'}
                  {saveState === 'saving' && <><FiLoader /> در حال ذخیره پیش‌نویس...</>}
                  {saveState === 'saved' && <><FiCheck /> پیش‌نویس ذخیره شد</>}
                  {saveState === 'error' && 'ذخیره نشد'}
                </span>
              </div>

              <div dir={activeLanguage.direction || 'rtl'}>
                <ProductRichTextEditor
                  key={`${articleId}:${activeLocale}`}
                  editorId={`article-content-${articleId}-${activeLocale}`}
                  value={value}
                  onChange={(nextValue) => {
                    setValue(nextValue);
                    scheduleSave(nextValue, activeLocale);
                  }}
                  onUploadImage={uploadLocalizedImage}
                  enableVideo
                  onUploadVideo={uploadArticleVideo}
                  placeholder={`متن کامل مقاله به زبان ${activeLanguage.nativeName} را اینجا بنویسید...`}
                  ariaLabel={`متن مقاله به زبان ${activeLanguage.nativeName}`}
                  imageAlt={`تصویر مقاله به زبان ${activeLanguage.nativeName}`}
                  direction={activeLanguage.direction || 'rtl'}
                />
              </div>

              <footer className="admin-product-description-translation__draft-bar">
                <div>
                  <small>
                    {activeTranslation?.hasUnpublishedChanges
                      ? 'تغییرات متن در پیش‌نویس ذخیره شده‌اند و با دکمه انتشار نهایی پایین فرم وارد سایت می‌شوند.'
                      : activeTranslation?.status === 'published'
                        ? 'متن منتشرشده این زبان به‌روز است.'
                        : 'متن این زبان به‌صورت پیش‌نویس ذخیره می‌شود.'}
                  </small>
                  {error && <strong role="alert">{error}</strong>}
                </div>
                <div>
                  <button type="button" className={`is-save ${saveState === 'saving' ? 'is-loading' : ''}`} onClick={() => flushLocale(activeLocale)} disabled={saveState === 'saving'}>
                    {saveState === 'saving' ? <FiLoader /> : <FiSave />} ذخیره پیش‌نویس
                  </button>
                </div>
              </footer>
            </div>
          ) : (
            <div className="admin-product-description-translation__empty">
              {error || 'زبان دیگری برای ترجمه فعال نشده است.'}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default AdminArticleContentTranslation;
